import React, { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { compose } from 'redux'
import { connect } from 'react-redux'
import * as flashActions from 'redux-flash'
import { Link, browserHistory } from 'react-router'
import { Card, RenderedHtml } from 'components'
import { selectors as globalSelectors } from '../global-reducer'
import { Spinner } from 'lp-components'
import { LEARNING_HUB_ROUTE } from 'config'
import * as apiActions from 'api-actions'
import * as LS from 'local-storage'
import { createCourseAttempt } from '../course-catalog/data'
import LearningHubCourseBookmarkButton from '../components/learning-hub-course-bookmark-button'
import { getLearningHubApiErrorMessage } from '../utils/api-error-message'
import { chapterOverviewPath } from '../utils/course-paths'

const propTypes = {
  params: PropTypes.shape({
    courseId: PropTypes.string.isRequired,
  }).isRequired,
  currentCourse: PropTypes.object,
  myCourses: PropTypes.array,
  fetchLearningHubCourse: PropTypes.func.isRequired,
  fetchLearningHubCoursePreview: PropTypes.func.isRequired,
  fetchLearningHubMyCourses: PropTypes.func.isRequired,
  startLearningHubCourse: PropTypes.func.isRequired,
  flashErrorMessage: PropTypes.func.isRequired,
  previewToken: PropTypes.string,
  isPreviewMode: PropTypes.bool,
}

const defaultProps = {
  currentCourse: undefined,
  myCourses: undefined,
  previewToken: undefined,
  isPreviewMode: false,
}

function ChapterCountIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden
    >
      <path
        d="M20 3C20.55 3 21 3.45 21 4V20C21 20.55 20.55 21 20 21H4C3.45 21 3 20.55 3 20V4C3 3.45 3.45 3 4 3H20ZM5 5V19H19V5H5ZM9 15V17H7V15H9ZM16 15C16.55 15 17 15.45 17 16C17 16.55 16.55 17 16 17H12C11.45 17 11 16.55 11 16C11 15.45 11.45 15 12 15H16ZM9 11V13H7V11H9ZM16 11C16.55 11 17 11.45 17 12C17 12.55 16.55 13 16 13H12C11.45 13 11 12.55 11 12C11 11.45 11.45 11 12 11H16ZM9 7V9H7V7H9ZM16 7C16.55 7 17 7.45 17 8C17 8.55 16.55 9 16 9H12C11.45 9 11 8.55 11 8C11 7.45 11.45 7 12 7H16Z"
        fill="currentColor"
      />
    </svg>
  )
}

function TotalLengthIcon() {
  return (
    <svg
      width="22"
      height="20"
      viewBox="0 0 22 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <path
        d="M10.75 18.837C16.2728 18.837 20.75 14.7881 20.75 9.79348C20.75 4.7989 16.2728 0.75 10.75 0.75C5.22715 0.75 0.75 4.7989 0.75 9.79348C0.75 14.7881 5.22715 18.837 10.75 18.837Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M7.52002 10.0014V8.49116C7.52002 6.61012 8.99002 5.84142 10.79 6.78195L12.24 7.5416L13.69 8.30125C15.49 9.24177 15.49 10.7792 13.69 11.7197L12.24 12.4793L10.79 13.239C8.99002 14.1795 7.52002 13.4108 7.52002 11.5298V10.0014Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeMiterlimit="10"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function CategoryIcon() {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <path
        d="M13.01 2.91982L18.91 5.53982C20.61 6.28982 20.61 7.52982 18.91 8.27982L13.01 10.8998C12.34 11.1998 11.24 11.1998 10.57 10.8998L4.67002 8.27982C2.97002 7.52982 2.97002 6.28982 4.67002 5.53982L10.57 2.91982C11.24 2.61982 12.34 2.61982 13.01 2.91982Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M3 11C3 11.84 3.63 12.81 4.4 13.15L11.19 16.17C11.71 16.4 12.3 16.4 12.81 16.17L19.6 13.15C20.37 12.81 21 11.84 21 11"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M3 16C3 16.93 3.55 17.77 4.4 18.15L11.19 21.17C11.71 21.4 12.3 21.4 12.81 21.17L19.6 18.15C20.45 17.77 21 16.93 21 16"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function CourseStoryPage({
  params,
  currentCourse,
  myCourses,
  fetchLearningHubCourse,
  fetchLearningHubCoursePreview,
  fetchLearningHubMyCourses,
  startLearningHubCourse,
  flashErrorMessage,
  previewToken: previewTokenProp,
  isPreviewMode = false,
}) {
  const courseId = Number(params.courseId)
  const isPreview =
    isPreviewMode ||
    (Number(LS.getPreviewCourseId()) === courseId && !!LS.getPreviewToken())
  const previewToken =
    previewTokenProp || (isPreview ? LS.getPreviewToken() : null)
  const [activeTab, setActiveTab] = useState('description')

  useEffect(() => {
    if (Number.isNaN(courseId)) return
    if (isPreview) {
      if (!previewToken) return
      fetchLearningHubCoursePreview(courseId, previewToken)
      return
    }
    fetchLearningHubCourse(courseId)
  }, [
    courseId,
    isPreview,
    previewToken,
    fetchLearningHubCourse,
    fetchLearningHubCoursePreview,
  ])

  useEffect(() => {
    if (isPreview) return
    if (myCourses === undefined) {
      fetchLearningHubMyCourses()
    }
  }, [isPreview, myCourses, fetchLearningHubMyCourses])

  const course =
    currentCourse && Number(currentCourse.id) === courseId
      ? currentCourse
      : undefined
  const chapters = useMemo(() => course?.chapters || [], [course])
  const startedCourseIds = useMemo(() => {
    if (!Array.isArray(myCourses)) return new Set()
    return new Set(
      myCourses
        .map((row) => Number(row?.course?.id))
        .filter((id) => !Number.isNaN(id))
    )
  }, [myCourses])
  const alreadyEnrolled = startedCourseIds.has(Number(course?.id))
  const totalMinutes = useMemo(
    () =>
      chapters.reduce(
        (chapterTotal, chapter) =>
          chapterTotal +
          (chapter.blocks || []).reduce(
            (blockTotal, block) =>
              blockTotal + Number(block.estimatedTime || 0),
            0
          ),
        0
      ),
    [chapters]
  )

  if (!course) {
    return (
      <div className="content-block-container learning-hub-course-detail">
        <Spinner />
      </div>
    )
  }

  return (
    <div className="content-block-container learning-hub-course-detail">
      {isPreview && (
        <div className="learning-hub-preview-banner" role="status">
          <p>
            You are viewing a Course Preview. Publish it to make the course
            publicly available.
          </p>
        </div>
      )}
      {!isPreview && (
        <nav
          className="learning-hub-course-detail__breadcrumb"
          aria-label="Breadcrumb"
        >
          <Link to={`${LEARNING_HUB_ROUTE}/course-catalog`}>
            Course Catalog
          </Link>
          <span>/</span>
          <span>{course.title}</span>
        </nav>
      )}

      <header className="learning-hub-course-detail__header">
        <h1>{course.title}</h1>
        <p>{course.instructor}</p>
      </header>

      <div className="learning-hub-course-detail__content-grid">
        <section className="learning-hub-course-detail__left">
          <img
            src={course.coverImage}
            alt={`${course.title} cover`}
            className="learning-hub-course-detail__cover"
          />

          <div className="learning-hub-course-detail__tabs">
            <button
              type="button"
              className={`learning-hub-course-detail__tab ${
                activeTab === 'description' ? 'is-active' : ''
              }`}
              onClick={() => setActiveTab('description')}
            >
              Description
            </button>
            <button
              type="button"
              className={`learning-hub-course-detail__tab ${
                activeTab === 'chapters' ? 'is-active' : ''
              }`}
              onClick={() => setActiveTab('chapters')}
            >
              Chapters
            </button>
          </div>

          <div className="learning-hub-course-detail__body">
            {activeTab === 'description' ? (
              <div>
                <h3>About Course</h3>
                <RenderedHtml>{course.description}</RenderedHtml>
              </div>
            ) : (
              <div className="learning-hub-course-detail__chapters">
                {chapters.length === 0 ? (
                  <p>No chapters available.</p>
                ) : (
                  chapters.map((chapter, idx) => (
                    <section
                      key={chapter.id}
                      className="learning-hub-course-detail__chapter"
                    >
                      <h3>
                        {`Chapter ${idx + 1} - ${chapter.title} (${(
                          chapter.blocks || []
                        ).reduce(
                          (sum, block) =>
                            sum + Number(block.estimatedTime || 0),
                          0
                        )} min)`}
                      </h3>
                      <ol>
                        {(chapter.blocks || []).map((block) => (
                          <li key={block.id}>
                            {block.title}
                            {block.estimatedTime
                              ? ` (${block.estimatedTime} min)`
                              : ''}
                          </li>
                        ))}
                      </ol>
                    </section>
                  ))
                )}
              </div>
            )}
          </div>
        </section>

        <Card className="learning-hub-course-detail__right">
          {isPreview ? (
            // In preview the admin isn't enrolling — there's no attempt to
            // create, no notifications to enqueue. Just jump straight into
            // the chapters/blocks tour under the preview route subtree.
            <div className="learning-hub-course-detail__action-row">
              <button
                type="button"
                className="button-primary learning-hub-course-detail__start"
                onClick={() =>
                  browserHistory.push(
                    chapterOverviewPath(course.id, { isPreview: true })
                  )
                }
              >
                Start Preview
              </button>
            </div>
          ) : (
            <div className="learning-hub-course-detail__action-row">
              <button
                type="button"
                className="button-primary learning-hub-course-detail__start"
                onClick={async () => {
                  if (alreadyEnrolled) {
                    try {
                      await startLearningHubCourse(course.id)
                    } catch (error) {
                      flashErrorMessage(
                        getLearningHubApiErrorMessage(
                          error,
                          'Unable to resume this course.'
                        )
                      )
                      return
                    }
                    createCourseAttempt(course.id)
                    browserHistory.push(
                      `${LEARNING_HUB_ROUTE}/course/${course.id}/chapters`
                    )
                    return
                  }
                  try {
                    await startLearningHubCourse(course.id)
                    createCourseAttempt(course.id)
                    fetchLearningHubMyCourses()
                    browserHistory.push(
                      `${LEARNING_HUB_ROUTE}/course/${course.id}/chapters`
                    )
                  } catch (error) {
                    flashErrorMessage(
                      getLearningHubApiErrorMessage(
                        error,
                        'Unable to start this course.'
                      )
                    )
                  }
                }}
              >
                {alreadyEnrolled ? 'Continue' : 'Start Course'}
              </button>
              <LearningHubCourseBookmarkButton
                courseId={course.id}
                courseTitle={course.title}
                className="learning-hub-course-detail__bookmark"
              />
            </div>
          )}

          <ul className="learning-hub-course-detail__meta">
            <li className="learning-hub-course-detail__meta-item">
              <span className="learning-hub-course-detail__meta-icon">
                <ChapterCountIcon />
              </span>
              <span>{`${chapters.length} Chapters`}</span>
            </li>
            <li className="learning-hub-course-detail__meta-item">
              <span className="learning-hub-course-detail__meta-icon">
                <TotalLengthIcon />
              </span>
              <span>
                Total Length: <strong>{`${totalMinutes} minutes`}</strong>
              </span>
            </li>
            <li className="learning-hub-course-detail__meta-item">
              <span className="learning-hub-course-detail__meta-icon">
                <CategoryIcon />
              </span>
              <span>
                Category: <strong>{course.category || 'Uncategorized'}</strong>
              </span>
            </li>
          </ul>
        </Card>
      </div>
    </div>
  )
}

CourseStoryPage.propTypes = propTypes
CourseStoryPage.defaultProps = defaultProps

function mapStateToProps(state) {
  return {
    currentCourse: globalSelectors.currentCourse(state),
    myCourses: globalSelectors.myCourses(state),
  }
}

const mapDispatchToProps = {
  fetchLearningHubCourse: apiActions.fetchLearningHubCourse,
  fetchLearningHubCoursePreview: apiActions.fetchLearningHubCoursePreview,
  fetchLearningHubMyCourses: apiActions.fetchLearningHubMyCourses,
  startLearningHubCourse: apiActions.startLearningHubCourse,
  flashErrorMessage: flashActions.flashErrorMessage,
}

export default compose(connect(mapStateToProps, mapDispatchToProps))(
  CourseStoryPage
)
